import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Link2, Plus, CheckCircle2, FileText, Send, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatINRShort, formatDate } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { UniverseCompanyDialog } from "@/components/UniverseCompanyDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function Chip({ label, value, tone = "slate", testid }) {
  const tones = { slate: "bg-slate-100 text-slate-700", amber: "bg-amber-50 text-amber-700", emerald: "bg-emerald-50 text-emerald-700" };
  return (
    <div className={`rounded-lg px-4 py-2 ${tones[tone]}`} data-testid={testid}>
      <span className="font-heading text-lg font-bold">{value}</span>
      <span className="text-xs ml-1.5">{label}</span>
    </div>
  );
}

export default function UniverseCleanup() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState("");
  const [addFor, setAddFor] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["universe-unlinked"],
    queryFn: async () => (await api.get("/master-data/unlinked")).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["universe-unlinked"] });
    qc.invalidateQueries({ queryKey: ["universe"] });
    qc.invalidateQueries({ queryKey: ["leads"] });
    qc.invalidateQueries({ queryKey: ["deals-list"] });
    qc.invalidateQueries({ queryKey: ["deals-kanban"] });
  };

  const link = async (row, m) => {
    setBusy(row.id);
    try {
      const path = row.kind === "lead" ? `/leads/${row.id}` : `/opportunities/${row.id}`;
      await api.put(path, { universe_id: m.id, universe_company: m.company });
      toast.success(`“${row.company}” linked to ${m.company} in the Universe`);
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const items = data?.items || [];

  return (
    <div data-testid="universe-cleanup-page">
      <PageHeader title="Universe Cleanup" subtitle="Leads & opportunities in your book that are not yet linked to a Universe company. Link an exact match in one click, or add the company to the Universe." />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Chip label="unlinked records" value={isLoading ? "—" : data?.total ?? 0} tone="amber" testid="cleanup-chip-total" />
        <Chip label="leads" value={isLoading ? "—" : data?.leads ?? 0} testid="cleanup-chip-leads" />
        <Chip label="opportunities" value={isLoading ? "—" : data?.opportunities ?? 0} testid="cleanup-chip-opps" />
        <Chip label="have an exact match" value={isLoading ? "—" : data?.with_match ?? 0} tone="emerald" testid="cleanup-chip-match" />
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div className="py-16 flex flex-col items-center gap-2 text-center" data-testid="cleanup-empty">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            <p className="font-heading font-semibold text-slate-800">All caught up</p>
            <p className="text-sm text-slate-500">Every lead and opportunity in your book is linked to the Universe.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                {["", "Company", "Project", "Value", "Stage / Status", "Owner", "Created", "Fix"].map((h) => (
                  <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow></TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={`${row.kind}-${row.id}`} data-testid={`cleanup-row-${row.id}`}>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${row.kind === "opportunity" ? "bg-indigo-50 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>
                        {row.kind === "opportunity" ? <Send className="h-3 w-3" /> : <FileText className="h-3 w-3" />} {row.kind === "opportunity" ? "Opportunity" : "Lead"}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium text-slate-800 min-w-[160px]">
                      <button onClick={() => navigate(row.kind === "lead" ? `/leads/${row.id}` : `/deals/${row.id}`)}
                        className="flex items-center gap-1.5 hover:text-brand transition-colors text-left" title="Open record" data-testid={`cleanup-open-${row.id}`}>
                        {row.company || "—"} <ExternalLink className="h-3 w-3 text-slate-300" />
                      </button>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">{row.project_name || "—"}</TableCell>
                    <TableCell className="font-mono text-sm whitespace-nowrap">{row.value ? formatINRShort(row.value) : "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap">{row.status || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-500 whitespace-nowrap">{row.owner_name || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-500 whitespace-nowrap">{formatDate(row.created_at)}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        {row.suggested && (
                          <Button size="sm" variant="outline" disabled={busy === row.id} onClick={() => link(row, row.suggested)}
                            className="h-8 gap-1.5 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`cleanup-link-${row.id}`}>
                            {busy === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Link to {row.suggested.company}
                          </Button>
                        )}
                        <Button size="sm" disabled={busy === row.id} onClick={() => setAddFor(row)}
                          className="h-8 gap-1.5 text-xs bg-amber-600 hover:bg-amber-700 text-white" data-testid={`cleanup-add-${row.id}`}>
                          <Plus className="h-3.5 w-3.5" /> Add to Universe
                        </Button>
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <UniverseCompanyDialog open={!!addFor} onOpenChange={(v) => !v && setAddFor(null)} skipDupGate
        initial={addFor ? {
          company: addFor.company,
          contact_person: addFor.end_client_name || "",
          contact_number: addFor.end_client_phone || "",
          email: addFor.end_client_email || "",
        } : {}}
        onSaved={(doc) => { const row = addFor; setAddFor(null); link(row, { id: doc.id, company: doc.company }); }} />
    </div>
  );
}
